import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";

export const VoiceListingButton = ({ onResult }) => {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        if (blob.size < 2000) {
          toast.error("Recording too short — describe the product for a few seconds");
          return;
        }
        setBusy(true);
        try {
          const form = new FormData();
          form.append("file", blob, mime.includes("webm") ? "voice.webm" : "voice.mp4");
          const { data } = await api.post("/ai/voice-listing", form, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 90000,
          });
          onResult(data);
          toast.success("Heard you! Listing drafted — review and tweak");
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || "Voice listing failed — try again");
        } finally {
          setBusy(false);
        }
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Microphone access denied — allow the mic to use voice listing");
    }
  };

  const stop = () => {
    recRef.current?.stop();
    setRecording(false);
  };

  return (
    <button
      type="button"
      onClick={recording ? stop : start}
      disabled={busy}
      data-testid="voice-listing-btn"
      aria-label={recording ? "Stop recording" : "Describe product by voice"}
      className={`inline-flex items-center gap-1.5 text-xs transition-colors disabled:opacity-60 ${
        recording ? "text-[#FF3B30] font-bold animate-pulse" : "text-[#007AFF] hover:underline"
      }`}
    >
      {busy ? (
        <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Transcribing &amp; writing…</>
      ) : recording ? (
        <><Square className="h-3.5 w-3.5" /> Stop — I'm done describing</>
      ) : (
        <><Mic className="h-3.5 w-3.5" /> Describe it by voice — Whisper listens, Gemini writes</>
      )}
    </button>
  );
};
